import { api } from '../core/client';
import { protectParam } from '../core/urlCrypto';
import { wirePhone } from '../core/wire';
import type {
  CustomerHomeScreen,
  QrMenuScreen,
  OrderTrackScreen,
  CustomerProfileScreen,
  RewardsWalletScreen
} from '../data/screen';
/**
 * Screen API — one GET returns a whole screen (design rule §2).
 *
 * Every customer screen is live against the ThaliOS Web API. The old per-screen mock
 * switch (VITE_USE_MOCK / VITE_LIVE_SCREENS) and the ../mock/customer payloads behind it
 * are gone: they defaulted to mock, so a build that simply forgot to set the flag served
 * a hardcoded customer ("Aarav Sharma", 1,240 pts, ₹540 wallet) as if it were real.
 */

/** The KSS Web API wraps every payload in a standard envelope. */
interface ApiEnvelope<T> {
  success: boolean;
  message: string | null;
  result: T;
}

/** GET a screen and unwrap the `Response<T>` envelope returned by the Web API. */
function getScreen<T>(path: string): Promise<T> {
  return api.get<ApiEnvelope<T>>(path).then((r) => r.data.result);
}

export const screenService = {
  getCustomerHome(): Promise<CustomerHomeScreen> {
    return getScreen<CustomerHomeScreen>('/ui/screen/customer-home');
  },

  async getQrMenu(tableCode: string): Promise<QrMenuScreen> {
    // No scanned table (takeaway) → a sentinel path segment so the route still resolves; the
    // backend returns the full menu with tableAvailable=false. An empty segment would 404.
    const seg = tableCode ? await protectParam(tableCode) : 'takeaway';
    return getScreen<QrMenuScreen>(`/ui/screen/qr/${seg}`);
  },

  async getOrderTrack(id: string): Promise<OrderTrackScreen> {
    return getScreen<OrderTrackScreen>(`/ui/screen/order-track/${await protectParam(id)}`);
  },

  getCustomerProfile(): Promise<CustomerProfileScreen> {
    return getScreen<CustomerProfileScreen>('/ui/screen/customer-profile');
  },

  getRewardsWallet(phone?: string): Promise<RewardsWalletScreen> {
    // The LAST ten digits: a bare strip leaves the "+91" on the display-formatted number the
    // auth store holds, which is then a different number from the same customer's elsewhere.
    const digits = wirePhone(phone);
    const q = digits ? `?phone=${digits}` : '';
    return getScreen<RewardsWalletScreen>(`/ui/screen/rewards-wallet${q}`);
  }
};
